import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, ClipboardCheck, ExternalLink, Pencil, Search, X } from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { useNr2jAutoRefresh } from '../../hooks/useNr2jAutoRefresh';
import apiService from '../../services/api';
import { formatCentsAsEuros, formatNr2jDate, formatNr2jDateOnly } from '../../utils/nr2jConstants';
import {
  NR2J_APPROVAL_DECISIONS,
  NR2J_APPROVAL_TYPE_FILTERS,
  approvalAmountCents,
  approvalCollaboratorOptions,
  approvalDocumentLabel,
  approvalDocumentStatusBadge,
  approvalStatusOptions,
  approvalUserName,
  filterApprovals,
  nr2jApprovalDecisionNeedsComment,
  nr2jApprovalTypeLabel,
  summarizeApprovals,
} from '../../utils/nr2jApprovals';
import { notifyNr2jDataChanged } from '../../utils/nr2jRealtime.js';
import { readAdminSessionUser } from '../../utils/settingsSessionUser.js';

function KpiCard({ title, value, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50',
    indigo: 'border-indigo-200 bg-indigo-50',
    emerald: 'border-emerald-200 bg-emerald-50',
  };
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tones[tone] || tones.slate}`}>
      <p className="text-sm text-gray-600">{title}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value ?? '—'}</p>
    </div>
  );
}

function StatusBadge({ approval }) {
  const badge = approvalDocumentStatusBadge(approval);
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex justify-between gap-4 py-2 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-medium text-gray-900">{children}</dd>
    </div>
  );
}

const DECISION_BUTTONS = [
  {
    id: 'APPROVED',
    label: 'Valider',
    icon: Check,
    className: 'bg-indigo-600 text-white hover:bg-indigo-700',
  },
  {
    id: 'CHANGES_REQUESTED',
    label: 'Demander des modifs',
    icon: Pencil,
    className: 'border border-amber-300 bg-amber-50 text-amber-950 hover:bg-amber-100',
  },
  {
    id: 'REJECTED',
    label: 'Refuser',
    icon: X,
    className: 'border border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100',
  },
];

function ApprovalDrawer({ approval, isOwnRequest, busy, onClose, onDecide }) {
  const [decision, setDecision] = useState(null);
  const [comment, setComment] = useState('');
  const doc = approval.document || {};
  const amount = approvalAmountCents(approval);
  const needsComment = nr2jApprovalDecisionNeedsComment(decision);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevOverflowX = document.body.style.overflowX;
    document.body.style.overflow = 'hidden';
    document.body.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.overflowX = prevOverflowX;
    };
  }, []);

  const submit = (nextDecision = decision) => {
    if (!nextDecision) return;
    if (nr2jApprovalDecisionNeedsComment(nextDecision) && !comment.trim()) {
      setDecision(nextDecision);
      return;
    }
    onDecide(approval, nextDecision, comment.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end overflow-hidden" data-testid="nr2j-approval-drawer">
      <div className="absolute inset-0 bg-gray-900/30" onClick={busy ? undefined : onClose} />
      <aside className="relative flex h-full w-full max-w-md shrink-0 flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {nr2jApprovalTypeLabel(approval.entityType)}
            </p>
            <h2 className="text-lg font-semibold text-gray-900">{approvalDocumentLabel(approval)}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {doc.organizationName ? (
                <p className="text-sm text-gray-600">{doc.organizationName}</p>
              ) : null}
              <StatusBadge approval={approval} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Résumé du document</h3>
            <dl className="mt-1 divide-y divide-gray-100">
              <DetailRow label="Type">{nr2jApprovalTypeLabel(approval.entityType)}</DetailRow>
              <DetailRow label="Numéro">{approvalDocumentLabel(approval)}</DetailRow>
              <DetailRow label="Client">
                {doc.organizationId ? (
                  <Link to={`/nr2j/clients/${doc.organizationId}`} className="text-indigo-700 hover:underline">
                    {doc.organizationName || 'Société'}
                  </Link>
                ) : '—'}
              </DetailRow>
              <DetailRow label="Montant HT">
                {doc.totalHtCents != null ? `${formatCentsAsEuros(doc.totalHtCents)} €` : '—'}
              </DetailRow>
              <DetailRow label="Montant TTC">
                {amount != null ? `${formatCentsAsEuros(amount)} €` : '—'}
              </DetailRow>
              <DetailRow label="Statut du document">
                <StatusBadge approval={approval} />
              </DetailRow>
              {doc.validUntil ? (
                <DetailRow label="Valable jusqu'au">{formatNr2jDateOnly(doc.validUntil)}</DetailRow>
              ) : null}
              {doc.dueAt ? (
                <DetailRow label="Échéance">{formatNr2jDateOnly(doc.dueAt)}</DetailRow>
              ) : null}
              {doc.sourceQuoteNumber ? (
                <DetailRow label="Devis d'origine">{doc.sourceQuoteNumber}</DetailRow>
              ) : null}
              {doc.invoiceNumber ? (
                <DetailRow label="Facture d'origine">{doc.invoiceNumber}</DetailRow>
              ) : null}
              {doc.reason ? <DetailRow label="Motif de l'avoir">{doc.reason}</DetailRow> : null}
              <DetailRow label="Soumis le">{formatNr2jDate(approval.requestedAt)}</DetailRow>
              <DetailRow label="Approbateur assigné">{approvalUserName(approval.assignedApprover)}</DetailRow>
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Soumis par</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{approvalUserName(approval.requestedBy)}</p>
            {approval.requestedBy?.email ? (
              <p className="text-xs text-gray-500">{approval.requestedBy.email}</p>
            ) : null}
          </div>

          {doc.href ? (
            <Link
              to={doc.href}
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-700 hover:underline"
            >
              Ouvrir le document <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          ) : null}

          {isOwnRequest ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Vous ne pouvez pas valider votre propre demande.
            </p>
          ) : (
            <div className="space-y-3">
              {needsComment ? (
                <div>
                  <label htmlFor="nr2j-approval-comment" className="text-sm font-medium text-gray-700">
                    Motif <span className="text-rose-600">*</span>
                  </label>
                  <textarea
                    id="nr2j-approval-comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    placeholder="Expliquez au collaborateur ce qui doit être revu…"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    data-testid="nr2j-approval-motif"
                  />
                  {!comment.trim() ? (
                    <p className="mt-1 text-xs text-rose-700">Un motif est obligatoire.</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {!isOwnRequest ? (
          <div className="space-y-2 border-t border-gray-100 px-5 py-4">
            {DECISION_BUTTONS.map((btn) => {
              const Icon = btn.icon;
              const active = decision === btn.id;
              const waitingMotif = nr2jApprovalDecisionNeedsComment(btn.id) && decision === btn.id;
              return (
                <button
                  key={btn.id}
                  type="button"
                  disabled={busy || (waitingMotif && !comment.trim())}
                  onClick={() => {
                    if (nr2jApprovalDecisionNeedsComment(btn.id)) {
                      if (decision !== btn.id) {
                        setDecision(btn.id);
                        return;
                      }
                      submit(btn.id);
                      return;
                    }
                    submit(btn.id);
                  }}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 ${btn.className} ${
                    active && needsComment ? 'ring-2 ring-offset-1 ring-indigo-400' : ''
                  }`}
                  data-testid={`nr2j-approval-decide-${btn.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {waitingMotif && comment.trim()
                    ? `Confirmer — ${btn.label}`
                    : waitingMotif
                      ? `${btn.label} (motif requis)`
                      : btn.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

export default function Nr2jApprovals() {
  const { toasts, toast, removeToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState([]);
  const [type, setType] = useState('');
  const [collaboratorId, setCollaboratorId] = useState('');
  const [statusLabel, setStatusLabel] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const sessionUserId = readAdminSessionUser()?.id || null;
  const deepLinkId = searchParams.get('id');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiService.listNr2jApprovals();
      setApprovals(res?.approvals || []);
    } catch (error) {
      if (!silent) toast.error(error?.message || 'Impossible de charger les documents à valider');
      if (!silent) setApprovals([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const silentReload = useCallback(() => {
    load({ silent: true });
  }, [load]);
  useNr2jAutoRefresh(silentReload, { enabled: true, intervalMs: 12000, runOnMount: false });

  useEffect(() => {
    if (!deepLinkId || loading) return;
    const match = approvals.find((row) => row.id === deepLinkId);
    if (match) setSelected(match);
  }, [deepLinkId, approvals, loading]);

  const summary = useMemo(() => summarizeApprovals(approvals), [approvals]);
  const collaborators = useMemo(() => approvalCollaboratorOptions(approvals), [approvals]);
  const statuses = useMemo(() => approvalStatusOptions(approvals), [approvals]);
  const rows = useMemo(
    () => filterApprovals(approvals, { type, collaboratorId, statusLabel, q }),
    [approvals, type, collaboratorId, statusLabel, q],
  );

  const closeDrawer = () => {
    setSelected(null);
    if (deepLinkId) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const decide = async (approval, decision, comment) => {
    setBusy(true);
    try {
      await apiService.decideNr2jApproval(approval.id, { decision, comment });
      toast.success(NR2J_APPROVAL_DECISIONS[decision]?.success || 'Décision enregistrée');
      closeDrawer();
      notifyNr2jDataChanged({ entityType: approval.entityType, action: decision });
      await load();
    } catch (error) {
      const code = error?.code || error?.error || error?.body?.code;
      if (code === 'APPROVAL_ENTITY_DELETED' || error?.status === 409) {
        toast.error(error?.message || 'Le document associé a été supprimé');
        closeDrawer();
        notifyNr2jDataChanged({ entityType: approval.entityType, action: 'CANCELLED' });
        await load();
      } else {
        toast.error(error?.message || 'Décision impossible');
      }
    } finally {
      setBusy(false);
    }
  };

  const drawerOpen = Boolean(selected);

  return (
    <div className="min-w-0 space-y-6 overflow-x-hidden" data-testid="nr2j-approvals" data-drawer-open={drawerOpen ? '1' : '0'}>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">À valider</h1>
        <p className="text-sm text-gray-600">Documents de vos collaborateurs en attente de validation managériale.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard title="Total en attente" value={loading ? '—' : summary.total} tone="amber" />
        <KpiCard title="Devis" value={loading ? '—' : summary.quotes} />
        <KpiCard title="Factures" value={loading ? '—' : summary.invoices} />
        <KpiCard title="Avoirs" value={loading ? '—' : summary.creditNotes} />
        <KpiCard
          title="Montant cumulé TTC"
          value={loading || summary.amountCents == null ? '—' : `${formatCentsAsEuros(summary.amountCents)} €`}
          tone="indigo"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par numéro, client ou collaborateur…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {NR2J_APPROVAL_TYPE_FILTERS.map((f) => {
            const count = f.id
              ? approvals.filter((row) => row.entityType === f.id).length
              : approvals.length;
            return (
              <button
                key={f.id || 'all'}
                type="button"
                onClick={() => setType(f.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  type === f.id
                    ? 'bg-indigo-600 text-white'
                    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {f.label} ({count})
              </button>
            );
          })}
        </div>
        <select
          value={collaboratorId}
          onChange={(e) => setCollaboratorId(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          aria-label="Collaborateur"
        >
          <option value="">Tous les collaborateurs</option>
          {collaborators.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <select
          value={statusLabel}
          onChange={(e) => setStatusLabel(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          aria-label="Statut"
        >
          <option value="">Tous les statuts</option>
          {statuses.map((label) => (
            <option key={label} value={label}>{label}</option>
          ))}
        </select>
      </div>

      <div className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Chargement…</p>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center">
            <ClipboardCheck className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">
              {approvals.length === 0 ? 'Aucun document en attente de validation.' : 'Aucun document pour ce filtre.'}
            </p>
          </div>
        ) : (
          <table className="w-full table-fixed divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className={`px-3 py-3 ${drawerOpen ? 'hidden lg:table-cell' : ''}`}>Type</th>
                <th className="px-3 py-3">Document</th>
                <th className="px-3 py-3">Collaborateur</th>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Montant</th>
                <th className={`px-3 py-3 ${drawerOpen ? 'hidden' : 'hidden xl:table-cell'}`}>Soumis le</th>
                <th className={`px-3 py-3 ${drawerOpen ? 'hidden' : ''}`}>Statut</th>
                <th className={`px-3 py-3 ${drawerOpen ? 'hidden' : ''}`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((approval) => {
                const amount = approvalAmountCents(approval);
                return (
                  <tr
                    key={approval.id}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      selected?.id === approval.id ? 'bg-indigo-50/60' : ''
                    }`}
                    onClick={() => setSelected(approval)}
                  >
                    <td className={`px-3 py-3 text-gray-600 ${drawerOpen ? 'hidden lg:table-cell' : ''}`}>
                      {nr2jApprovalTypeLabel(approval.entityType)}
                    </td>
                    <td className="min-w-0 truncate px-3 py-3 font-medium text-gray-900">
                      {approvalDocumentLabel(approval)}
                    </td>
                    <td className="min-w-0 px-3 py-3">
                      <div className="truncate font-medium text-gray-900">{approvalUserName(approval.requestedBy)}</div>
                      {!drawerOpen && approval.requestedBy?.email ? (
                        <div className="truncate text-xs text-gray-500">{approval.requestedBy.email}</div>
                      ) : null}
                    </td>
                    <td className="min-w-0 truncate px-3 py-3 text-gray-600">
                      {approval.document?.organizationName || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 tabular-nums text-gray-600">
                      {amount != null ? `${formatCentsAsEuros(amount)} €` : '—'}
                    </td>
                    <td className={`px-3 py-3 text-gray-600 ${drawerOpen ? 'hidden' : 'hidden xl:table-cell'}`}>
                      {formatNr2jDate(approval.requestedAt)}
                    </td>
                    <td className={`px-3 py-3 ${drawerOpen ? 'hidden' : ''}`}><StatusBadge approval={approval} /></td>
                    <td className={`px-3 py-3 ${drawerOpen ? 'hidden' : ''}`}>
                      <button
                        type="button"
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(approval);
                        }}
                      >
                        Examiner
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected ? (
        <ApprovalDrawer
          key={selected.id}
          approval={selected}
          isOwnRequest={Boolean(sessionUserId) && selected.requestedByUserId === sessionUserId}
          busy={busy}
          onClose={closeDrawer}
          onDecide={decide}
        />
      ) : null}

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
